import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  MessageSquare,
  X,
  Send,
  Loader2,
  Sparkles,
  ArrowUpRight,
  CheckCircle2,
  AlertTriangle,
  RotateCcw,
} from "lucide-react";
import { VINI } from "@/constants/testIds/vini";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INTENT_CHIPS = [
  { slug: "pmo-setup", label: "Set up a new PMO" },
  { slug: "rescue", label: "Rescue a stuck program" },
  { slug: "app-rationalization", label: "Rationalize my app estate" },
  { slug: "vendor", label: "Manage vendors better" },
  { slug: "staffing", label: "Hire PMO / SAP talent" },
  { slug: "other", label: "Something else" },
];

const GREETING =
  "Hi, I'm ViNi — PMONexa's AI concierge. What brings you here today? Pick a topic or just tell me what's on your mind.";

function genSessionId() {
  return `vini-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export default function ViniChat() {
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(() => {
    if (typeof window === "undefined") return genSessionId();
    const existing = window.localStorage.getItem("vini_session");
    if (existing) return existing;
    const sid = genSessionId();
    window.localStorage.setItem("vini_session", sid);
    return sid;
  });
  const [messages, setMessages] = useState([
    { role: "assistant", content: GREETING },
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [leadStatus, setLeadStatus] = useState({ state: "idle", error: "" });
  const [lead, setLead] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    role: "",
    intent: "",
  });
  const listRef = useRef(null);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, sending]);

  const sendToVini = async (text) => {
    const userMsg = { role: "user", content: text };
    setMessages((m) => [...m, userMsg]);
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/chat/vini`, {
        session_id: sessionId,
        message: text,
      });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (e) {
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content:
            "Sorry — I had trouble responding just then. Tap 'Talk to a partner' below and we'll reach out directly.",
        },
      ]);
    } finally {
      setSending(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    sendToVini(text);
  };

  const handleQuickReply = (chip) => {
    if (sending) return;
    setLead((l) => ({ ...l, intent: chip.label }));
    sendToVini(`I'm interested in: ${chip.label}.`);
  };

  const handleReset = () => {
    const sid = genSessionId();
    localStorage.setItem("vini_session", sid);
    setSessionId(sid);
    setMessages([{ role: "assistant", content: GREETING }]);
    setShowLeadForm(false);
    setLeadStatus({ state: "idle", error: "" });
    setLead({ name: "", email: "", phone: "", company: "", role: "", intent: "" });
  };

  const submitLead = async (e) => {
    e.preventDefault();
    setLeadStatus({ state: "loading", error: "" });
    try {
      await axios.post(`${API}/leads`, { ...lead, session_id: sessionId });
      setLeadStatus({ state: "success", error: "" });
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content:
            "Perfect — I've flagged your request to our partner team. Someone from PMONexa will reach out within one business day. Anything else you'd like to share in the meantime?",
        },
      ]);
    } catch (err) {
      const detail =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please try again.";
      setLeadStatus({ state: "error", error: String(detail) });
    }
  };

  const showIntroChips = useMemo(() => messages.length <= 1, [messages]);

  return (
    <>
      {/* Floating action button */}
      <AnimatePresence>
        {!open && (
          <motion.button
            key="vini-fab"
            data-testid={VINI.fab}
            onClick={() => setOpen(true)}
            initial={{ opacity: 0, y: 20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.9 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.97 }}
            className="fixed bottom-6 right-6 z-[60] flex items-center gap-3 pl-3 pr-5 py-3 bg-[#0F172A] border border-amber-500/40 text-white shadow-[0_18px_50px_-12px_rgba(234,88,12,0.45)] hover:border-amber-500 transition-colors"
          >
            <span className="relative w-10 h-10 flex items-center justify-center bg-gradient-to-br from-amber-500 to-orange-600">
              <MessageSquare size={18} className="text-black" />
              <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-emerald-400 ring-2 ring-[#0F172A]" />
            </span>
            <div className="text-left leading-tight">
              <div className="text-[11px] tracking-[0.22em] uppercase text-amber-500 font-semibold">
                Ask ViNi
              </div>
              <div className="text-sm text-slate-300">Your PMO concierge</div>
            </div>
          </motion.button>
        )}
      </AnimatePresence>

      {/* Chat panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            key="vini-panel"
            data-testid={VINI.panel}
            initial={{ opacity: 0, y: 30, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.97 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="fixed bottom-6 right-6 z-[60] w-[min(96vw,420px)] max-h-[min(82vh,720px)] flex flex-col bg-[#050608] border border-white/10 shadow-2xl"
            style={{ borderRadius: 0 }}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-white/10 bg-gradient-to-b from-[#0F172A] to-[#050608]">
              <div className="flex items-center gap-3">
                <div className="relative w-10 h-10 flex items-center justify-center bg-gradient-to-br from-amber-500 to-orange-600">
                  <Sparkles size={16} className="text-black" />
                  <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-emerald-400 ring-2 ring-[#0F172A]" />
                </div>
                <div>
                  <div className="font-display text-base tracking-tight">
                    Vi<span className="text-amber-500">Ni</span>
                  </div>
                  <div className="text-[10px] tracking-[0.22em] text-slate-400 uppercase">
                    PMONexa · AI Concierge
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  data-testid={VINI.reset}
                  onClick={handleReset}
                  className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-amber-500 transition-colors"
                  aria-label="Reset conversation"
                  title="Reset conversation"
                >
                  <RotateCcw size={14} />
                </button>
                <button
                  data-testid={VINI.close}
                  onClick={() => setOpen(false)}
                  className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-white transition-colors"
                  aria-label="Close chat"
                >
                  <X size={16} />
                </button>
              </div>
            </div>

            {/* Messages */}
            <div
              ref={listRef}
              data-testid={VINI.messagesList}
              className="flex-1 overflow-y-auto px-4 py-5 space-y-4 bg-[#050608]"
            >
              {messages.map((m, i) => (
                <div
                  key={i}
                  data-testid={VINI.message(i)}
                  className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[88%] px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
                      m.role === "user"
                        ? "bg-amber-500 text-black font-medium"
                        : "bg-[#0F172A] border border-white/10 text-slate-200"
                    }`}
                    style={{ borderRadius: 0 }}
                  >
                    {m.content}
                  </div>
                </div>
              ))}

              {showIntroChips && (
                <div className="pt-2 grid grid-cols-1 gap-2">
                  {INTENT_CHIPS.map((c) => (
                    <button
                      key={c.slug}
                      data-testid={VINI.quickReply(c.slug)}
                      onClick={() => handleQuickReply(c)}
                      disabled={sending}
                      className="text-left text-sm px-4 py-3 border border-white/10 hover:border-amber-500/60 hover:text-amber-500 text-slate-300 transition-colors flex items-center justify-between"
                    >
                      <span>{c.label}</span>
                      <ArrowUpRight size={14} className="text-amber-500" />
                    </button>
                  ))}
                </div>
              )}

              {sending && (
                <div className="flex items-center gap-2 text-slate-500 text-sm">
                  <Loader2 size={14} className="animate-spin text-amber-500" />
                  ViNi is thinking…
                </div>
              )}
            </div>

            {/* Lead form (collapsible) */}
            <AnimatePresence>
              {showLeadForm && (
                <motion.form
                  data-testid={VINI.leadForm}
                  onSubmit={submitLead}
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="border-t border-white/10 bg-[#0a0d14] overflow-hidden"
                >
                  <div className="p-4 space-y-3">
                    <div className="text-[10px] tracking-[0.25em] uppercase text-amber-500 font-semibold">
                      Talk to a partner
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <input
                        required
                        placeholder="Full name *"
                        data-testid={VINI.leadName}
                        value={lead.name}
                        onChange={(e) => setLead({ ...lead, name: e.target.value })}
                        className="col-span-2 bg-transparent border-b border-white/15 text-sm py-2 outline-none focus:border-amber-500 placeholder:text-slate-500 text-white"
                      />
                      <input
                        required
                        type="email"
                        placeholder="Email *"
                        data-testid={VINI.leadEmail}
                        value={lead.email}
                        onChange={(e) => setLead({ ...lead, email: e.target.value })}
                        className="col-span-2 sm:col-span-1 bg-transparent border-b border-white/15 text-sm py-2 outline-none focus:border-amber-500 placeholder:text-slate-500 text-white"
                      />
                      <input
                        required
                        placeholder="Phone *"
                        data-testid={VINI.leadPhone}
                        value={lead.phone}
                        onChange={(e) => setLead({ ...lead, phone: e.target.value })}
                        className="col-span-2 sm:col-span-1 bg-transparent border-b border-white/15 text-sm py-2 outline-none focus:border-amber-500 placeholder:text-slate-500 text-white"
                      />
                      <input
                        placeholder="Company"
                        data-testid={VINI.leadCompany}
                        value={lead.company}
                        onChange={(e) => setLead({ ...lead, company: e.target.value })}
                        className="col-span-2 sm:col-span-1 bg-transparent border-b border-white/15 text-sm py-2 outline-none focus:border-amber-500 placeholder:text-slate-500 text-white"
                      />
                      <input
                        placeholder="Role"
                        data-testid={VINI.leadRole}
                        value={lead.role}
                        onChange={(e) => setLead({ ...lead, role: e.target.value })}
                        className="col-span-2 sm:col-span-1 bg-transparent border-b border-white/15 text-sm py-2 outline-none focus:border-amber-500 placeholder:text-slate-500 text-white"
                      />
                      <input
                        placeholder="Topic / Intent"
                        data-testid={VINI.leadIntent}
                        value={lead.intent}
                        onChange={(e) => setLead({ ...lead, intent: e.target.value })}
                        className="col-span-2 bg-transparent border-b border-white/15 text-sm py-2 outline-none focus:border-amber-500 placeholder:text-slate-500 text-white"
                      />
                    </div>

                    {leadStatus.state === "success" && (
                      <div
                        data-testid={VINI.leadSuccess}
                        className="flex items-center gap-2 text-xs text-emerald-400 mt-2"
                      >
                        <CheckCircle2 size={14} />
                        Sent — a partner will reach out within 1 business day.
                      </div>
                    )}
                    {leadStatus.state === "error" && (
                      <div
                        data-testid={VINI.leadError}
                        className="flex items-center gap-2 text-xs text-rose-400 mt-2"
                      >
                        <AlertTriangle size={14} /> {leadStatus.error}
                      </div>
                    )}

                    <div className="flex items-center justify-between pt-2">
                      <button
                        type="button"
                        onClick={() => setShowLeadForm(false)}
                        className="text-xs text-slate-500 hover:text-slate-300 uppercase tracking-[0.2em]"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={leadStatus.state === "loading"}
                        data-testid={VINI.leadSubmit}
                        className="pmx-btn-primary !py-2 !px-4 !text-[11px] disabled:opacity-60"
                      >
                        {leadStatus.state === "loading" ? (
                          <>
                            <Loader2 size={12} className="animate-spin" /> Sending…
                          </>
                        ) : (
                          <>
                            Connect Me <ArrowUpRight size={12} />
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </motion.form>
              )}
            </AnimatePresence>

            {/* Composer */}
            <form
              onSubmit={handleSubmit}
              className="border-t border-white/10 bg-[#0a0d14] p-3"
            >
              <div className="flex items-center gap-2">
                <input
                  data-testid={VINI.input}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Ask ViNi anything…"
                  className="flex-1 bg-transparent border border-white/10 px-3 py-2.5 text-sm text-white outline-none focus:border-amber-500/60 placeholder:text-slate-500"
                  disabled={sending}
                />
                <button
                  type="submit"
                  data-testid={VINI.send}
                  disabled={sending || !input.trim()}
                  className="w-10 h-10 bg-amber-500 text-black flex items-center justify-center hover:bg-amber-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  aria-label="Send message"
                >
                  <Send size={15} />
                </button>
              </div>
              <button
                type="button"
                data-testid={VINI.openLeadForm}
                onClick={() => setShowLeadForm((v) => !v)}
                className="mt-3 w-full text-[11px] tracking-[0.22em] uppercase text-amber-500 hover:text-amber-400 transition-colors flex items-center justify-center gap-2"
              >
                {showLeadForm ? "Hide" : "Talk to a partner"}
                <ArrowUpRight size={12} />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
